import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import {
  History, Search, Filter, Calendar, User, Eye, RefreshCw,
  ChevronDown, ChevronUp, FileText, DollarSign, Package, Users
} from 'lucide-react';

const HistoricalEntriesViewer = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [filters, setFilters] = useState({
    transaction_type: '',
    start_date: '',
    end_date: '',
    entered_by: '',
    migration_mode: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [pagination, setPagination] = useState({ total: 0, limit: 50, offset: 0, pages: 0 });

  useEffect(() => {
    loadEntries();
    loadSummary();
  }, [filters, pagination.offset]);

  const loadEntries = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.transaction_type) params.append('transaction_type', filters.transaction_type);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.entered_by) params.append('entered_by', filters.entered_by);
      if (filters.migration_mode !== '') params.append('migration_mode', filters.migration_mode);
      params.append('limit', pagination.limit);
      params.append('offset', pagination.offset);

      const res = await api.get(`/historical/entries?${params.toString()}`);
      setEntries(res.data.entries);
      setPagination(res.data.pagination);
    } catch (err) {
      toast.error('Failed to load historical entries');
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      const res = await api.get('/historical/entries/summary');
      setSummary(res.data);
    } catch (err) {
      console.error('Failed to load summary:', err);
    }
  };

  const viewEntry = async (id) => {
    try {
      const res = await api.get(`/historical/entries/${id}`);
      setSelectedEntry(res.data);
    } catch (err) {
      toast.error('Failed to load entry details');
    }
  };

  const getTransactionIcon = (type) => {
    const icons = {
      sale: '💰',
      expense: '💸',
      stock_received: '📦',
      stock_issued: '📤',
      inventory_adjustment: '⚖️',
      customer_payment: '💵',
      supplier_payment: '🏦',
      payroll: '👔',
      appointment: '📅',
      patient_record: '🏥',
      asset_purchase: '🏗️',
      bank_transaction: '💳',
      leave_records: '🏖️',
      other: '📋'
    };
    return icons[type] || '📋';
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-KE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <History className="w-6 h-6 text-amber-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Historical Entries</h1>
            <p className="text-sm text-gray-500">View and audit all backdated transactions</p>
          </div>
        </div>
        <button
          onClick={() => { loadEntries(); loadSummary(); }}
          className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center"
        >
          <RefreshCw size={14} className="mr-2" />
          Refresh
        </button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Entries</p>
                <p className="text-2xl font-bold text-gray-900">{summary.totals?.total_entries || 0}</p>
              </div>
              <FileText className="w-8 h-8 text-blue-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Amount</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(summary.totals?.grand_total)}</p>
              </div>
              <DollarSign className="w-8 h-8 text-green-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Users</p>
                <p className="text-2xl font-bold text-gray-900">{summary.totals?.unique_users || 0}</p>
              </div>
              <Users className="w-8 h-8 text-purple-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Migration Mode</p>
                <p className="text-2xl font-bold text-gray-900">{summary.migration_mode ? 'ON' : 'OFF'}</p>
              </div>
              <Package className="w-8 h-8 text-amber-500" />
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg border mb-6">
        <div className="p-4 flex items-center justify-between cursor-pointer" onClick={() => setShowFilters(!showFilters)}>
          <div className="flex items-center space-x-2">
            <Filter size={18} className="text-gray-400" />
            <h3 className="font-medium text-gray-800">Filters</h3>
          </div>
          {showFilters ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </div>
        
        {showFilters && (
          <div className="p-4 border-t grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Transaction Type</label>
              <select
                value={filters.transaction_type}
                onChange={(e) => setFilters({...filters, transaction_type: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">All Types</option>
                <option value="sale">Sale</option>
                <option value="expense">Expense</option>
                <option value="stock_received">Stock Received</option>
                <option value="stock_issued">Stock Issued</option>
                <option value="inventory_adjustment">Inventory Adjustment</option>
                <option value="customer_payment">Customer Payment</option>
                <option value="supplier_payment">Supplier Payment</option>
                <option value="payroll">Payroll</option>
                <option value="appointment">Appointment</option>
                <option value="patient_record">Patient Record</option>
                <option value="asset_purchase">Asset Purchase</option>
                <option value="bank_transaction">Bank Transaction</option>
                <option value="leave_records">Leave Records</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={filters.start_date}
                onChange={(e) => setFilters({...filters, start_date: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={filters.end_date}
                onChange={(e) => setFilters({...filters, end_date: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Migration Mode</label>
              <select
                value={filters.migration_mode}
                onChange={(e) => setFilters({...filters, migration_mode: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">All</option>
                <option value="true">Migration</option>
                <option value="false">Normal</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={loadEntries}
                className="w-full px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm"
              >
                Apply Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Entries Table */}
      <div className="bg-white rounded-lg border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Transaction Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entered By</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mode</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600 mx-auto"></div>
                  </td>
                </tr>
              ) : entries.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                    No historical entries found
                  </td>
                </tr>
              ) : (
                entries.map((entry) => (
                  <tr key={entry.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-2">
                        <span className="text-xl">{getTransactionIcon(entry.transaction_type)}</span>
                        <span className="text-sm font-medium text-gray-900 capitalize">
                          {entry.transaction_type.replace(/_/g, ' ')}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {formatDate(entry.transaction_date)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 max-w-xs truncate">
                      {entry.description || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {formatCurrency(entry.amount)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {entry.entered_by_name || '-'}
                    </td>
                    <td className="px-4 py-3">
                      {entry.migration_mode ? (
                        <span className="px-2 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-medium">
                          Migration
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                          Normal
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => viewEntry(entry.id)}
                        className="text-amber-600 hover:text-amber-700 text-sm font-medium flex items-center justify-end"
                      >
                        <Eye size={14} className="mr-1" />
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="px-4 py-3 border-t flex items-center justify-between">
            <div className="text-sm text-gray-500">
              Showing {pagination.offset + 1} to {Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total} entries
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setPagination({...pagination, offset: Math.max(0, pagination.offset - pagination.limit)})}
                disabled={pagination.offset === 0}
                className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPagination({...pagination, offset: pagination.offset + pagination.limit})}
                disabled={pagination.offset + pagination.limit >= pagination.total}
                className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Entry Detail Modal */}
      {selectedEntry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Entry Details</h2>
              <button
                onClick={() => setSelectedEntry(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            <div className="p-6 space-y-6">
              {/* Transaction Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Transaction Type</label>
                  <p className="text-sm text-gray-900 capitalize">{selectedEntry.transaction_type?.replace(/_/g, ' ')}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Transaction Date</label>
                  <p className="text-sm text-gray-900">{formatDate(selectedEntry.transaction_date)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Entry Date</label>
                  <p className="text-sm text-gray-900">{formatDate(selectedEntry.entry_date)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Entered By</label>
                  <p className="text-sm text-gray-900">{selectedEntry.entered_by_name || '-'}</p>
                </div>
                <div className="col-span-2">
                  <label className="text-sm font-medium text-gray-500">Description</label>
                  <p className="text-sm text-gray-900">{selectedEntry.description || '-'}</p>
                </div>
                {selectedEntry.reason_for_backdating && (
                  <div className="col-span-2">
                    <label className="text-sm font-medium text-gray-500">Reason for Backdating</label>
                    <p className="text-sm text-gray-900">{selectedEntry.reason_for_backdating}</p>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-gray-500">Amount</label>
                  <p className="text-sm text-gray-900 font-medium">{formatCurrency(selectedEntry.amount)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Quantity</label>
                  <p className="text-sm text-gray-900">{selectedEntry.quantity || '-'}</p>
                </div>
              </div>

              {/* Audit Trail */}
              {selectedEntry.audit_trail && selectedEntry.audit_trail.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Audit Trail</h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2 max-h-60 overflow-y-auto">
                    {selectedEntry.audit_trail.map((audit, idx) => (
                      <div key={idx} className="text-sm border-b border-gray-200 pb-2 last:border-0">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-700">{audit.action_type}</span>
                          <span className="text-xs text-gray-500">{formatDate(audit.changed_at)}</span>
                        </div>
                        {audit.field_name && (
                          <p className="text-gray-600">Field: {audit.field_name}</p>
                        )}
                        {audit.reason && (
                          <p className="text-gray-600 italic">Reason: {audit.reason}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HistoricalEntriesViewer;